import { getBearerToken } from "../../../api/postCall/bearerToken";
import { API_ENDPOINTS } from "../../../enums/apiUrls";
import { logDataToConsole, logError } from "../../../utils/customLogger";
import type { SupportTicketDetailApiResponse } from "../supportTicketTypes";
import { parseSupportTicketApiError } from "../utils/parseSupportTicketApiError";

/**
 * Request body for POST /pharmacy/support-tickets/get-details.
 */
export interface FetchSupportTicketDetailsParams {
  org_id: string;
  ticket_id: string;
}

/**
 * Fetches full detail for a single support ticket.
 *
 * @param params - Organization id and ticket id.
 * @returns Ticket detail including comments and attachments.
 */
export const fetchSupportTicketDetails = async (
  params: FetchSupportTicketDetailsParams,
): Promise<SupportTicketDetailApiResponse> => {
  const body = {
    org_id: params.org_id,
    ticket_id: params.ticket_id,
  };

  try {
    const token = await getBearerToken();
    const response = await fetch(API_ENDPOINTS.GET_SUPPORT_TICKET_DETAILS, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(
        await parseSupportTicketApiError(
          response,
          "Failed to load support ticket details",
        ),
      );
    }

    const result = (await response.json()) as SupportTicketDetailApiResponse;
    logDataToConsole("fetchSupportTicketDetails", result);
    return result;
  } catch (error) {
    logError(error as Error, "Error in fetchSupportTicketDetails");
    throw error;
  }
};
